import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { PDFDocument } from "pdf-lib";
import Swal from "sweetalert2";
import { applyImageFilters, warpPerspective, autoDetectDocumentCorners, removeBackgroundEdges } from "../../utils/imageFilters";

const pdfjsLib = (() => {
  try { const m = require("pdfjs-dist/legacy/build/pdf"); m.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`; return m; }
  catch (e) { return null; }
})();

function createDefaultFilterConfig() {
  return { brightness: 0, contrast: 0, saturation: 0, sharpness: 0, filterType: "scanner-color" };
}

function getDefaultFileState() {
  return {
    filterConfig: createDefaultFilterConfig(),
    rotation: 0,
    flipH: false,
    flipV: false,
    perspectivePoints: null,
    cropAspect: "free",
    undoStack: [],
    redoStack: [],
    warpApplied: false,
  };
}

const S = {
  overlay: { position: "fixed", inset: 0, backgroundColor: "rgba(15,23,42,0.6)", zIndex: 9999, display: "flex", alignItems: "stretch", justifyContent: "center" },
  modal: { backgroundColor: "#f8fafc", borderRadius: "0", width: "100vw", height: "100dvh", maxHeight: "100dvh", display: "flex", flexDirection: "column", overflow: "hidden", fontFamily: "system-ui, -apple-system, sans-serif", position: "relative" },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 24px", borderBottom: "1px solid #e2e8f0", backgroundColor: "#fff" },
  body: { flex: 1, overflow: "auto", WebkitOverflowScrolling: "touch", backgroundColor: "#f1f5f9" },
  card: { backgroundColor: "#fff", borderRadius: "12px", border: "1px solid #e2e8f0", padding: "16px" },
  label: { fontWeight: 600, fontSize: "11px", color: "#64748b", marginBottom: "10px", textTransform: "uppercase", letterSpacing: "0.5px" },
  btn: (active, primary) => ({
    padding: "8px 16px", borderRadius: "8px", border: primary ? "none" : "1px solid #e2e8f0",
    fontSize: "12px", fontWeight: 600, cursor: "pointer",
    backgroundColor: primary ? "#2563eb" : active ? "#eff6ff" : "#f1f5f9",
    color: primary ? "#fff" : active ? "#2563eb" : "#64748b",
    borderColor: active ? "#2563eb" : "#e2e8f0",
    transition: "all 0.15s",
  }),
};

function to6Points(pts) {
  if (!pts || pts.length === 0) return null;
  if (pts.length === 6) return pts;
  if (pts.length < 4) return null;
  const lm = { x: (pts[0].x + pts[3].x) / 2, y: (pts[0].y + pts[3].y) / 2 };
  const rm = { x: (pts[1].x + pts[2].x) / 2, y: (pts[1].y + pts[2].y) / 2 };
  return [pts[0], pts[1], pts[2], pts[3], lm, rm];
}

const perspLabels = ["TL", "TR", "BR", "BL", "L-MID", "R-MID"];

export default function DocumentEditor({ open, onClose, onSave, title, acceptedTypes = ["pdf", "image"] }) {
  const [files, setFiles] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [fileStates, setFileStates] = useState({});
  const [activeTab, setActiveTab] = useState("scanner");
  const [isProcessing, setIsProcessing] = useState(false);
  const [previewUrls, setPreviewUrls] = useState({});
  const [showReview, setShowReview] = useState(false);
  const [activeMode, setActiveMode] = useState("perspective");
  const [imgRenderSize, setImgRenderSize] = useState({ w: 0, h: 0 });
  const [sliderPos, setSliderPos] = useState(50);
  const [compareMode, setCompareMode] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [showReorder, setShowReorder] = useState(false);
  const [reorderedFiles, setReorderedFiles] = useState([]);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const touchDragRef = useRef(null);
  const fileInputRef = useRef(null);
  const addMoreRef = useRef(null);
  const perspImageRef = useRef(null);

  const activeFile = files[activeIndex];
  const fileState = fileStates[activeFile && activeFile.id] || getDefaultFileState();
  const activeState = activeFile ? fileState : getDefaultFileState();
  const { filterConfig, rotation, flipH, flipV, perspectivePoints, cropAspect, undoStack, redoStack } = activeState;
  const previewUrl = activeFile && previewUrls[activeFile.id] ? previewUrls[activeFile.id] : null;
  const hasFiles = files.length > 0;

  const updateActiveState = useCallback((partial) => {
    if (!activeFile) return;
    setFileStates((prev) => ({
      ...prev,
      [activeFile.id]: { ...(prev[activeFile.id] || getDefaultFileState()), ...partial },
    }));
  }, [activeFile]);

  useEffect(() => {
    if (!open) {
      setFiles([]); setActiveIndex(0); setFileStates({});
      setPreviewUrls((prev) => { Object.values(prev).forEach((u) => URL.revokeObjectURL(u)); return {}; });
      setShowReview(false); setActiveMode("edit"); setSliderPos(50); setCompareMode(false);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (activeFile && activeMode === "edit") applyCurrentFilter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFile, filterConfig, rotation, flipH, flipV, activeMode, cropAspect, perspectivePoints]);

  useEffect(() => {
    if (activeMode === "perspective" && activeFile && !perspectivePoints) {
      const def = to6Points(autoDetectDocumentCorners(activeFile.width, activeFile.height));
      updateActiveState({ perspectivePoints: def });
    }
    if (activeMode === "perspective") setTimeout(updateRenderSize, 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFile?.id, activeMode]);

  const pushHistory = () => {
    updateActiveState({
      undoStack: [...undoStack, { filterConfig: { ...filterConfig }, rotation, flipH, flipV, perspectivePoints: perspectivePoints ? [...perspectivePoints] : null, cropAspect }],
      redoStack: [],
    });
  };

  const handleUndo = () => {
    if (undoStack.length === 0) return;
    const prev = [...undoStack];
    const state = prev.pop();
    updateActiveState({
      filterConfig: state.filterConfig, rotation: state.rotation, flipH: state.flipH, flipV: state.flipV,
      perspectivePoints: state.perspectivePoints, cropAspect: state.cropAspect || "free",
      undoStack: prev,
      redoStack: [...redoStack, { filterConfig: { ...filterConfig }, rotation, flipH, flipV, perspectivePoints: perspectivePoints ? [...perspectivePoints] : null, cropAspect }],
    });
  };

  const handleRedo = () => {
    if (redoStack.length === 0) return;
    const next = [...redoStack];
    const state = next.pop();
    updateActiveState({
      filterConfig: state.filterConfig, rotation: state.rotation, flipH: state.flipH, flipV: state.flipV,
      perspectivePoints: state.perspectivePoints, cropAspect: state.cropAspect || "free",
      undoStack: [...undoStack, { filterConfig: { ...filterConfig }, rotation, flipH, flipV, perspectivePoints: perspectivePoints ? [...perspectivePoints] : null, cropAspect }],
      redoStack: next,
    });
  };

  const handleReset = () => {
    pushHistory();
    const def = activeFile ? to6Points(autoDetectDocumentCorners(activeFile.width, activeFile.height)) : null;
    updateActiveState({ ...getDefaultFileState(), perspectivePoints: def });
    setActiveMode("perspective");
  };

  const getCropRegion = (w, h, aspect) => {
    if (aspect === "free" || aspect === "original") return null;
    let targetRatio;
    switch (aspect) {
      case "1:1": targetRatio = 1; break;
      case "4:3": targetRatio = 4 / 3; break;
      case "16:9": targetRatio = 16 / 9; break;
      case "A4": targetRatio = 1 / 1.414; break;
      default: return null;
    }
    let cw, ch;
    if (w / h > targetRatio) { cw = Math.round(h * targetRatio); ch = h; }
    else { cw = w; ch = Math.round(w / targetRatio); }
    return { x: Math.round((w - cw) / 2), y: Math.round((h - ch) / 2), width: cw, height: ch };
  };

  const applyCurrentFilter = useCallback(() => {
    if (!activeFile) return;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const srcCanvas = document.createElement("canvas");
      srcCanvas.width = img.naturalWidth;
      srcCanvas.height = img.naturalHeight;
      const srcCtx = srcCanvas.getContext("2d", { willReadFrequently: true });
      if (!srcCtx) return;
      srcCtx.drawImage(img, 0, 0);
      const warpedCanvas = document.createElement("canvas");
      if (perspectivePoints && perspectivePoints.length >= 4) {
        warpPerspective(srcCtx, img.naturalWidth, img.naturalHeight, warpedCanvas, perspectivePoints);
        removeBackgroundEdges(warpedCanvas);
      } else {
        warpedCanvas.width = img.naturalWidth;
        warpedCanvas.height = img.naturalHeight;
        const wc = warpedCanvas.getContext("2d", { willReadFrequently: true });
        if (wc) wc.drawImage(srcCanvas, 0, 0);
      }
      let preFilterCanvas = warpedCanvas;
      const cropRegion = getCropRegion(warpedCanvas.width, warpedCanvas.height, cropAspect);
      if (cropRegion) {
        preFilterCanvas = document.createElement("canvas");
        preFilterCanvas.width = cropRegion.width;
        preFilterCanvas.height = cropRegion.height;
        const cropCtx = preFilterCanvas.getContext("2d", { willReadFrequently: true });
        if (cropCtx) cropCtx.drawImage(warpedCanvas, cropRegion.x, cropRegion.y, cropRegion.width, cropRegion.height, 0, 0, cropRegion.width, cropRegion.height);
      }
      const dstCanvas = document.createElement("canvas");
      applyImageFilters(preFilterCanvas, dstCanvas, filterConfig, rotation, flipH, flipV);
      const maxDim = 4000;
      let outputCanvas = dstCanvas;
      if (dstCanvas.width > maxDim || dstCanvas.height > maxDim) {
        const scale = maxDim / Math.max(dstCanvas.width, dstCanvas.height);
        const sc = document.createElement("canvas");
        sc.width = Math.round(dstCanvas.width * scale);
        sc.height = Math.round(dstCanvas.height * scale);
        const sctx = sc.getContext("2d", { willReadFrequently: true });
        if (sctx) { sctx.imageSmoothingEnabled = true; sctx.imageSmoothingQuality = "high"; sctx.drawImage(dstCanvas, 0, 0, sc.width, sc.height); outputCanvas = sc; }
      }
      const fileId = activeFile.id;
      if (previewUrls[fileId]) URL.revokeObjectURL(previewUrls[fileId]);
      outputCanvas.toBlob((blob) => { if (blob) setPreviewUrls((prev) => ({ ...prev, [fileId]: URL.createObjectURL(blob) })); }, "image/jpeg", 0.95);
    };
    img.src = activeFile.url;
  }, [activeFile, filterConfig, rotation, flipH, flipV, perspectivePoints, cropAspect]);

  const fileToDimensions = (file, url) => new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => resolve({ w: 800, h: 600 });
    img.src = url;
  });

  const makeFileEntry = async (file) => {
    const url = URL.createObjectURL(file);
    const dim = await fileToDimensions(file, url);
    return { id: `doc_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`, name: file.name, file, url, width: dim.w, height: dim.h };
  };

  const addFiles = async (fileList) => {
    const imageFiles = fileList.filter((f) => f.type.startsWith("image/"));
    if (imageFiles.length === 0) return;
    const newFiles = await Promise.all(imageFiles.map(makeFileEntry));
    const detectPromises = newFiles.map((f) => new Promise((resolve) => {
      const img = new Image();
      img.onload = async () => {
        const ourResult = autoDetectDocumentCorners(f.width, f.height, img);
        if (ourResult) { resolve(ourResult); return; }
        if (window.scanic) {
          try {
            const result = await window.scanic.scanDocument(img, { mode: "detect" });
            if (result && result.success && result.corners) {
              const c = result.corners;
              if (c.topLeft && c.topRight && c.bottomRight && c.bottomLeft) {
                const nw = img.naturalWidth || f.width;
                const nh = img.naturalHeight || f.height;
                const pts = to6Points([
                  { x: Math.max(0, Math.min(1, c.topLeft.x / nw)), y: Math.max(0, Math.min(1, c.topLeft.y / nh)) },
                  { x: Math.max(0, Math.min(1, c.topRight.x / nw)), y: Math.max(0, Math.min(1, c.topRight.y / nh)) },
                  { x: Math.max(0, Math.min(1, c.bottomRight.x / nw)), y: Math.max(0, Math.min(1, c.bottomRight.y / nh)) },
                  { x: Math.max(0, Math.min(1, c.bottomLeft.x / nw)), y: Math.max(0, Math.min(1, c.bottomLeft.y / nh)) },
                ]);
                resolve(pts);
                return;
              }
            }
          } catch (e) {
            console.warn("Scanic detection also failed:", e);
          }
        }
        resolve([{ x: 0.02, y: 0.02 }, { x: 0.98, y: 0.02 }, { x: 0.98, y: 0.98 }, { x: 0.02, y: 0.98 }]);
      };
      img.onerror = () => resolve(null);
      img.src = f.url;
    }));
    const detectedList = await Promise.all(detectPromises);
    setFileStates((prev) => {
      const next = { ...prev };
      newFiles.forEach((f, i) => {
        const pts = detectedList[i];
        next[f.id] = pts ? { ...getDefaultFileState(), perspectivePoints: to6Points(pts) } : getDefaultFileState();
      });
      return next;
    });
    const prevLen = files.length;
    setFiles((prev) => [...prev, ...newFiles]);
    if (prevLen === 0) setActiveIndex(0);
    else setActiveIndex(prevLen);
    setActiveMode("perspective");
  };

  const docTypes = ["application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
  const isDocFile = (f) => docTypes.includes(f.type) || /\.(doc|docx)$/i.test(f.name);
  const xlsxTypes = ["application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"];
  const isXlsxFile = (f) => xlsxTypes.includes(f.type) || /\.(xls|xlsx)$/i.test(f.name);
  const csvTypes = ["text/csv", "application/csv"];
  const isCsvFile = (f) => csvTypes.includes(f.type) || /\.csv$/i.test(f.name);
  const isTxtFile = (f) => f.type === "text/plain" || /\.txt$/i.test(f.name);
  const isDirectConvertFile = (f) => isXlsxFile(f) || isCsvFile(f) || isTxtFile(f);

  const isTypeAccepted = (f) => {
    if (acceptedTypes.includes("pdf") && f.type === "application/pdf") return true;
    if (acceptedTypes.includes("image") && f.type.startsWith("image/")) return true;
    if ((acceptedTypes.includes("doc") || acceptedTypes.includes("docx")) && isDocFile(f)) return true;
    if ((acceptedTypes.includes("xls") || acceptedTypes.includes("xlsx")) && isXlsxFile(f)) return true;
    if (acceptedTypes.includes("csv") && isCsvFile(f)) return true;
    if (acceptedTypes.includes("txt") && isTxtFile(f)) return true;
    return false;
  };

  const getAcceptAttr = () => {
    const attrs = [];
    if (acceptedTypes.includes("image")) { attrs.push("image/*"); attrs.push(".webp"); }
    if (acceptedTypes.includes("pdf")) attrs.push("application/pdf");
    if (acceptedTypes.includes("doc") || acceptedTypes.includes("docx")) attrs.push(...docTypes, ".doc", ".docx");
    if (acceptedTypes.includes("xls") || acceptedTypes.includes("xlsx")) attrs.push(".xls", ".xlsx");
    if (acceptedTypes.includes("csv")) attrs.push(".csv", "text/csv");
    if (acceptedTypes.includes("txt")) attrs.push(".txt", "text/plain");
    return attrs.join(",");
  };

  const getAcceptedText = () => {
    const typeLabels = {
      image: "JPG, PNG, WEBP images",
      pdf: "PDF files",
      doc: "Word files",
      docx: "Word files",
      xls: "Excel files",
      xlsx: "Excel files",
      csv: "CSV files",
      txt: "Text files",
    };
    const parts = [];
    acceptedTypes.forEach((t) => {
      const key = t.toLowerCase();
      if (typeLabels[key]) {
        if (!parts.includes(typeLabels[key])) parts.push(typeLabels[key]);
      } else {
        parts.push(`${key.toUpperCase()} files`);
      }
    });
    if (parts.length === 0) return "Upload files";
    if (parts.length === 1) return `Supports ${parts[0]}`;
    return `Supports ${parts.slice(0, -1).join(", ")} & ${parts[parts.length - 1]}`;
  };

  const handleFileSelect = (e) => {
    const rawFiles = Array.from(e.target.files || []);
    const rejectedFiles = rawFiles.filter((f) => !isTypeAccepted(f));
    if (rejectedFiles.length > 0) {
      const names = rejectedFiles.map((f) => f.name).join(", ");
      const accepted = getAcceptedText();
      Swal.fire({ icon: "error", title: "Unsupported File", text: `${names} — ${accepted}`, confirmButtonColor: "#2563eb" });
      e.target.value = "";
      return;
    }
    const allFiles = rawFiles.filter(isTypeAccepted);
    const docFiles = allFiles.filter((f) => isDocFile(f));
    const imageFiles = allFiles.filter((f) => f.type.startsWith("image/"));
    const pdfFiles = allFiles.filter((f) => f.type === "application/pdf");
    const directFiles = allFiles.filter((f) => isDirectConvertFile(f));
    const passThroughFiles = [...docFiles, ...directFiles];
    if (docFiles.length > 1) { Swal.fire({ icon: "error", title: "Upload Error", text: "Only one Word file can be uploaded at a time.", confirmButtonColor: "#2563eb" }); e.target.value = ""; return; }
    if (docFiles.length === 1 && allFiles.length > 1) { Swal.fire({ icon: "error", title: "Upload Error", text: "Word files must be uploaded separately from images and PDFs.", confirmButtonColor: "#2563eb" }); e.target.value = ""; return; }
    if (imageFiles.length > 0 && pdfFiles.length > 0) { Swal.fire({ icon: "error", title: "Upload Error", text: "You cannot upload images and PDFs together. Please upload only images or only PDFs.", confirmButtonColor: "#2563eb" }); e.target.value = ""; return; }
    if (passThroughFiles.length > 0) {
      const file = passThroughFiles[0];
      const r = new FileReader();
      r.onloadend = () => { onSave(r.result); onClose(); };
      r.onerror = () => { Swal.fire({ icon: "error", title: "Processing Failed", text: "Failed to process file.", confirmButtonColor: "#2563eb" }); };
      r.readAsDataURL(file);
      e.target.value = "";
      return;
    }
    if (allFiles.length > 0) setPendingFiles((prev) => [...prev, ...allFiles]);
    e.target.value = "";
  };
  const handleDrop = (e) => {
    e.preventDefault();
    const rawFiles = Array.from(e.dataTransfer.files || []);
    const rejectedFiles = rawFiles.filter((f) => !isTypeAccepted(f));
    if (rejectedFiles.length > 0) {
      const names = rejectedFiles.map((f) => f.name).join(", ");
      const accepted = getAcceptedText();
      Swal.fire({ icon: "error", title: "Unsupported File", text: `${names} — ${accepted}`, confirmButtonColor: "#2563eb" });
      return;
    }
    const allFiles = rawFiles.filter(isTypeAccepted);
    const docFiles = allFiles.filter((f) => isDocFile(f));
    const imageFiles = allFiles.filter((f) => f.type.startsWith("image/"));
    const pdfFiles = allFiles.filter((f) => f.type === "application/pdf");
    const directFiles = allFiles.filter((f) => isDirectConvertFile(f));
    const passThroughFiles = [...docFiles, ...directFiles];
    if (docFiles.length > 1) { Swal.fire({ icon: "error", title: "Upload Error", text: "Only one Word file can be uploaded at a time.", confirmButtonColor: "#2563eb" }); return; }
    if (docFiles.length === 1 && allFiles.length > 1) { Swal.fire({ icon: "error", title: "Upload Error", text: "Word files must be uploaded separately from images and PDFs.", confirmButtonColor: "#2563eb" }); return; }
    if (imageFiles.length > 0 && pdfFiles.length > 0) { Swal.fire({ icon: "error", title: "Upload Error", text: "You cannot upload images and PDFs together. Please upload only images or only PDFs.", confirmButtonColor: "#2563eb" }); return; }
    if (passThroughFiles.length > 0) {
      const file = passThroughFiles[0];
      const r = new FileReader();
      r.onloadend = () => { onSave(r.result); onClose(); };
      r.onerror = () => { Swal.fire({ icon: "error", title: "Processing Failed", text: "Failed to process file.", confirmButtonColor: "#2563eb" }); };
      r.readAsDataURL(file);
      return;
    }
    if (allFiles.length > 0) setPendingFiles((prev) => [...prev, ...allFiles]);
  };

  const pdfToImages = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const pages = [];
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const vp = page.getViewport({ scale: 2 });
      const canvas = document.createElement("canvas");
      canvas.width = vp.width;
      canvas.height = vp.height;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) continue;
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      await page.render({ canvasContext: ctx, viewport: vp }).promise;
      const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", 0.92));
      if (blob) pages.push(blob);
    }
    return pages;
  };

  const imageToPngBlob = (file) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = img.naturalWidth; c.height = img.naturalHeight;
      const ctx = c.getContext("2d", { willReadFrequently: true });
      if (!ctx) { reject(new Error("No canvas context")); return; }
      ctx.drawImage(img, 0, 0);
      c.toBlob((blob) => blob ? resolve(blob) : reject(new Error("Blob failed")), "image/png");
    };
    img.onerror = () => reject(new Error("Image load failed"));
    img.src = URL.createObjectURL(file);
  });

  const imageToCanvasBlob = (file) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = img.naturalWidth;
      c.height = img.naturalHeight;
      const ctx = c.getContext("2d", { willReadFrequently: true });
      if (!ctx) { reject(new Error("No canvas context")); return; }
      ctx.drawImage(img, 0, 0);
      c.toBlob((blob) => blob ? resolve(blob) : reject(new Error("Blob failed")), "image/jpeg", 0.95);
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => { URL.revokeObjectURL(img.src); reject(new Error("Image load failed")); };
    img.src = URL.createObjectURL(file);
  });

  const filesToPdf = async (fileList) => {
    const pdfDoc = await PDFDocument.create();
    for (const file of fileList) {
      if (file.type === "application/pdf") {
        const arrayBuffer = await file.arrayBuffer();
        let srcPdf = null;
        // Try a plain load first. Encrypted PDFs (e.g. AES-256 / V5 R6) throw here; pdf-lib cannot
        // decrypt them, so a mere copyPages would embed still-encrypted streams and render as blank
        // pages. In that case rasterize via PDF.js below instead.
        try { srcPdf = await PDFDocument.load(arrayBuffer); } catch (e) { srcPdf = null; }
        if (srcPdf) {
          const pages = await pdfDoc.copyPages(srcPdf, srcPdf.getPageIndices());
          pages.forEach((page) => pdfDoc.addPage(page));
        } else {
          const pageBlobs = await pdfToImages(file);
          for (const blob of pageBlobs) {
            const imgBuf = await blob.arrayBuffer();
            const img = await pdfDoc.embedJpg(imgBuf);
            const pg = pdfDoc.addPage([img.width, img.height]);
            pg.drawImage(img, { x: 0, y: 0, width: img.width, height: img.height });
          }
        }
      } else if (file.type.startsWith("image/")) {
        const exifBlob = await imageToCanvasBlob(file);
        const exifBuf = await exifBlob.arrayBuffer();
        const img = await pdfDoc.embedJpg(exifBuf);
        const pg = pdfDoc.addPage([img.width, img.height]);
        pg.drawImage(img, { x: 0, y: 0, width: img.width, height: img.height });
      } else {
        const pngBlob = await imageToPngBlob(file);
        const pngBuf = await pngBlob.arrayBuffer();
        const img = await pdfDoc.embedPng(pngBuf);
        const pg = pdfDoc.addPage([img.width, img.height]);
        pg.drawImage(img, { x: 0, y: 0, width: img.width, height: img.height });
      }
    }
    const bytes = await pdfDoc.save();
    return new Blob([bytes], { type: "application/pdf" });
  };

  const handleFilesEdit = async () => {
    setIsProcessing(true);
    try {
      const imageFiles = [];
      for (const file of pendingFiles) {
        if (file.type === "application/pdf") {
          const blobs = await pdfToImages(file);
          blobs.forEach((blob) => imageFiles.push(new File([blob], `${file.name.replace(/\.pdf$/i, "")}_${imageFiles.length + 1}.jpg`, { type: "image/jpeg" })));
        } else {
          imageFiles.push(file);
        }
      }
      await addFiles(imageFiles);
    } catch (err) { console.error("File processing failed:", err); Swal.fire({ icon: "error", title: "Processing Failed", text: "Failed to process files. Please try again.", confirmButtonColor: "#2563eb" }); }
    finally { setPendingFiles([]); setIsProcessing(false); }
  };

  const handleFilesDirect = () => {
    const docFiles = pendingFiles.filter((f) => isDocFile(f));
    const directFiles = pendingFiles.filter((f) => isXlsxFile(f) || isCsvFile(f) || isTxtFile(f));
    const passThroughFiles = [...docFiles, ...directFiles];
    if (passThroughFiles.length > 0) {
      const file = passThroughFiles[0];
      setIsProcessing(true);
      const r = new FileReader();
      r.onloadend = () => {
        onSave(r.result);
        onClose();
      };
      const typeName = isDocFile(file) ? "Word" : isXlsxFile(file) ? "Excel" : isCsvFile(file) ? "CSV" : "text";
      r.onerror = () => { Swal.fire({ icon: "error", title: "Processing Failed", text: `Failed to process ${typeName} file.`, confirmButtonColor: "#2563eb" }); setIsProcessing(false); };
      r.readAsDataURL(file);
      return;
    }
    setReorderedFiles([...pendingFiles]);
    setShowReorder(true);
  };

  const handleConfirmReorder = async () => {
    if (reorderedFiles.length === 0) { Swal.fire({ icon: "warning", title: "No Files", text: "No files to process.", confirmButtonColor: "#2563eb" }); return; }
    setIsProcessing(true);
    try {
      const pdfBlob = await filesToPdf(reorderedFiles);
      const base64Result = await new Promise((resolve) => { const r = new FileReader(); r.onloadend = () => resolve(r.result); r.readAsDataURL(pdfBlob); });
      onSave(base64Result);
      onClose();
    } catch (err) { console.error("Direct upload failed:", err); Swal.fire({ icon: "error", title: "Upload Failed", text: "Failed to process files. Please try again.", confirmButtonColor: "#2563eb" }); }
    finally { setShowReorder(false); setPendingFiles([]); setIsProcessing(false); }
  };

  const handleReorderTouchStart = (idx) => {
    setDragIndex(idx);
    setDragOverIndex(null);
  };

  const handleReorderTouchMove = (e) => {
    if (dragIndex === null) return;
    const touch = e.touches[0];
    const elem = document.elementFromPoint(touch.clientX, touch.clientY);
    if (elem) {
      const item = elem.closest("[data-reorder-idx]");
      if (item) {
        const overIdx = parseInt(item.getAttribute("data-reorder-idx"));
        if (overIdx !== dragOverIndex) setDragOverIndex(overIdx);
      } else {
        if (dragOverIndex !== null) setDragOverIndex(null);
      }
    }
  };

  const handleReorderTouchEnd = () => {
    if (dragIndex !== null && dragOverIndex !== null && dragIndex !== dragOverIndex) {
      reorderPending(dragIndex, dragOverIndex);
    }
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const reorderPending = (fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    setReorderedFiles((prev) => {
      const copy = [...prev];
      const [moved] = copy.splice(fromIdx, 1);
      copy.splice(toIdx, 0, moved);
      return copy;
    });
  };

  const removeFile = (index) => {
    const removed = files[index];
    if (removed) {
      if (previewUrls[removed.id]) URL.revokeObjectURL(previewUrls[removed.id]);
      setPreviewUrls((prev) => { const n = { ...prev }; delete n[removed.id]; return n; });
      setFileStates((prev) => { const n = { ...prev }; delete n[removed.id]; return n; });
    }
    setFiles((prev) => {
      const u = prev.filter((_, i) => i !== index);
      if (u.length === 0) { onClose(); return []; }
      return u;
    });
    setFiles((prev) => {
      if (prev.length === 0) setActiveIndex(0);
      else if (activeIndex >= prev.length) setActiveIndex(prev.length - 1);
      return prev;
    });
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const switchToFile = (idx) => {
    setActiveIndex(idx);
    setZoom(1);
    setPan({ x: 0, y: 0 });
    const target = files[idx];
    if (target) {
      const st = fileStates[target.id];
      if (activeMode !== "perspective") {
        setActiveMode(st && st.warpApplied ? "edit" : "perspective");
      }
    }
  };

  const reorderFiles = (fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    setFiles((prev) => {
      const copy = [...prev];
      const [moved] = copy.splice(fromIdx, 1);
      copy.splice(toIdx, 0, moved);
      return copy;
    });
    let newIdx = activeIndex;
    if (fromIdx < toIdx) {
      if (activeIndex === fromIdx) newIdx = toIdx;
      else if (activeIndex > fromIdx && activeIndex <= toIdx) newIdx = activeIndex - 1;
    } else {
      if (activeIndex === fromIdx) newIdx = toIdx;
      else if (activeIndex >= toIdx && activeIndex < fromIdx) newIdx = activeIndex + 1;
    }
    setActiveIndex(newIdx);
  };

  const handleDragStart = (idx, e) => {
    setDragIndex(idx);
    setDragOverIndex(null);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (idx, e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (idx !== dragIndex) setDragOverIndex(idx);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleThumbDrop = (idx, e) => {
    e.preventDefault();
    if (dragIndex !== null && dragIndex !== idx) reorderFiles(dragIndex, idx);
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleTouchStart = (idx, e) => {
    setDragIndex(idx);
    setDragOverIndex(null);
    touchDragRef.current = { startIdx: idx };
  };

  const handleTouchMove = (e) => {
    if (dragIndex === null) return;
    const touch = e.touches[0];
    const elem = document.elementFromPoint(touch.clientX, touch.clientY);
    if (elem) {
      const thumbEl = elem.closest("[data-thumb-idx]");
      if (thumbEl) {
        const overIdx = parseInt(thumbEl.getAttribute("data-thumb-idx"));
        if (overIdx !== dragOverIndex) setDragOverIndex(overIdx);
      } else {
        if (dragOverIndex !== null) setDragOverIndex(null);
      }
    }
  };

  const handleTouchEnd = () => {
    if (dragIndex !== null && dragOverIndex !== null && dragIndex !== dragOverIndex) {
      reorderFiles(dragIndex, dragOverIndex);
    }
    setDragIndex(null);
    setDragOverIndex(null);
    touchDragRef.current = null;
  };

  const updateRenderSize = () => {
    if (perspImageRef.current) {
      setImgRenderSize({ w: perspImageRef.current.clientWidth, h: perspImageRef.current.clientHeight });
    }
  };

  const handleSaveAsPdf = async () => {
    if (files.length === 0) return;
    setIsProcessing(true);
    try {
      const pdfDoc = await PDFDocument.create();
      for (const file of files) {
        const st = fileStates[file.id] || getDefaultFileState();
        const { imgData, outW, outH } = await new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => {
            const srcC = document.createElement("canvas");
            srcC.width = img.naturalWidth; srcC.height = img.naturalHeight;
            const sctx = srcC.getContext("2d", { willReadFrequently: true });
            if (!sctx) { reject(new Error("No ctx")); return; }
            sctx.drawImage(img, 0, 0);
            let wc = document.createElement("canvas");
            if (st.perspectivePoints && st.perspectivePoints.length >= 4) {
              warpPerspective(sctx, img.naturalWidth, img.naturalHeight, wc, st.perspectivePoints);
              removeBackgroundEdges(wc);
            } else {
              wc.width = img.naturalWidth; wc.height = img.naturalHeight;
              const wctx = wc.getContext("2d", { willReadFrequently: true }); if (wctx) wctx.drawImage(srcC, 0, 0);
            }
            let cc = wc;
            const region = getCropRegion(wc.width, wc.height, st.cropAspect);
            if (region) {
              cc = document.createElement("canvas");
              cc.width = region.width; cc.height = region.height;
              const cctx = cc.getContext("2d", { willReadFrequently: true });
              if (cctx) cctx.drawImage(wc, region.x, region.y, region.width, region.height, 0, 0, region.width, region.height);
            }
            const dc = document.createElement("canvas");
            applyImageFilters(cc, dc, st.filterConfig, st.rotation, st.flipH, st.flipV);
            dc.toBlob((blob) => {
              if (!blob) { reject(new Error("Blob")); return; }
              const r = new FileReader();
              r.onloadend = () => resolve({ imgData: r.result, outW: dc.width, outH: dc.height });
              r.readAsArrayBuffer(blob);
            }, "image/jpeg", 0.95);
          };
          img.onerror = () => reject(new Error("Img fail"));
          img.src = file.url;
        });
        const eImg = await pdfDoc.embedJpg(new Uint8Array(imgData));
        const pg = pdfDoc.addPage([outW, outH]);
        pg.drawImage(eImg, { x: 0, y: 0, width: outW, height: outH });
      }
      const pdfBytes = await pdfDoc.save();
      const pdfBlob = new Blob([pdfBytes], { type: "application/pdf" });
      const base64 = await new Promise((resolve) => { const r = new FileReader(); r.onloadend = () => resolve(r.result); r.readAsDataURL(pdfBlob); });
      onSave(base64);
      onClose();
    } catch (err) { console.error("PDF failed:", err); Swal.fire({ icon: "error", title: "PDF Export Failed", text: "Failed to create PDF. Please try again.", confirmButtonColor: "#2563eb" }); }
    finally { setIsProcessing(false); }
  };

  const handleFilterTypeSelect = (type) => { pushHistory(); updateActiveState({ filterConfig: { ...filterConfig, filterType: type } }); };
  const handleSliderChange = (key, value) => { pushHistory(); updateActiveState({ filterConfig: { ...filterConfig, [key]: value } }); };
  const handleRotate = () => { pushHistory(); updateActiveState({ rotation: (rotation + 90) % 360 }); };
  const handleFlipH = () => { pushHistory(); updateActiveState({ flipH: !flipH }); };
  const handleFlipV = () => { pushHistory(); updateActiveState({ flipV: !flipV }); };

  const enterPerspectiveMode = () => {
    setActiveMode("perspective");
    if (!perspectivePoints && activeFile) {
      const def = to6Points(autoDetectDocumentCorners(activeFile.width, activeFile.height));
      updateActiveState({ perspectivePoints: def });
    }
    setTimeout(updateRenderSize, 50);
  };

  const handlePanStart = (e) => {
    if (zoom <= 1) return;
    const p = e.touches ? { x: e.touches[0].clientX, y: e.touches[0].clientY } : { x: e.clientX, y: e.clientY };
    panStartRef.current = { x: p.x, y: p.y, panX: pan.x, panY: pan.y };
    setIsPanning(true);
  };

  const handlePanMove = (e) => {
    if (!isPanning || zoom <= 1) return;
    e.preventDefault();
    const p = e.touches ? { x: e.touches[0].clientX, y: e.touches[0].clientY } : { x: e.clientX, y: e.clientY };
    const dx = p.x - panStartRef.current.x;
    const dy = p.y - panStartRef.current.y;
    setPan({ x: panStartRef.current.panX + dx, y: panStartRef.current.panY + dy });
  };

  const handlePanEnd = () => { setIsPanning(false); };

  const handlePerspPointerDown = (idx, e) => {
    e.preventDefault();
    e.stopPropagation();
    const imgEl = perspImageRef.current;
    if (!imgEl) return;
    const onMove = (ev) => {
      if (!imgEl) return;
      const r = imgEl.getBoundingClientRect();
      let cx = Math.max(0, Math.min(r.width, ev.clientX - r.left));
      let cy = Math.max(0, Math.min(r.height, ev.clientY - r.top));
      updateActiveState({
        perspectivePoints: (perspectivePoints || []).map((pt, i) => i === idx ? { x: cx / r.width, y: cy / r.height } : { ...pt }),
      });
    };
    const onUp = () => { window.removeEventListener("pointermove", onMove); window.removeEventListener("pointerup", onUp); };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const handleApplyWarp = () => { pushHistory(); updateActiveState({ warpApplied: true }); setActiveMode("edit"); setCompareMode(false); setZoom(1); setPan({ x: 0, y: 0 }); };

  const handleResetPersp = () => {
    if (activeFile) updateActiveState({ perspectivePoints: to6Points(autoDetectDocumentCorners(activeFile.width, activeFile.height)) });
  };

  if (!open) return null;

  const filterPresets = [
    { type: "original", label: "Original", desc: "No filters applied" },
    { type: "magic", label: "Magic Color", desc: "Enhances text and whitens pages" },
    { type: "bw", label: "B&W Document", desc: "Crisp threshold binary scanning" },
    { type: "high-contrast", label: "High Contrast", desc: "Maximizes legibility" },
    { type: "grayscale", label: "Grayscale", desc: "Classic neutral document look" },
    { type: "shadow-free", label: "Shadow Removal", desc: "Smooths local illumination" },
  ];

  const aspectRatios = [
    { ratio: "free", label: "Free Crop" },
    { ratio: "original", label: "Original Ratio" },
    { ratio: "1:1", label: "Passport Square (1:1)" },
    { ratio: "4:3", label: "Standard ID (4:3)" },
    { ratio: "16:9", label: "Wide Frame (16:9)" },
    { ratio: "A4", label: "A4 Document (1:1.41)" },
  ];

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return "0 Bytes";
    const k = 1024; const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  const SVG = {
    upload: <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>,
    undo: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" /></svg>,
    redo: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" /></svg>,
    compare: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="12" y1="3" x2="12" y2="21" /></svg>,
    plus: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>,
    rotate: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2"><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" /></svg>,
    flipH: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2"><path d="M3 12h18" /><polyline points="12 5 19 12 12 19" /></svg>,
    flipV: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2"><path d="M12 3v18" /><polyline points="5 12 12 19 19 12" /></svg>,
    scanner: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 7V5a2 2 0 0 1 2-2h2" /><path d="M17 3h2a2 2 0 0 1 2 2v2" /><path d="M21 17v2a2 2 0 0 1-2 2h-2" /><path d="M7 21H5a2 2 0 0 1-2-2v-2" /><line x1="7" y1="12" x2="17" y2="12" /></svg>,
    filters: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>,
    sliders: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" /><line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" /><line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" /><line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" /><line x1="17" y1="16" x2="23" y2="16" /></svg>,
    layout: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="21" x2="9" y2="9" /></svg>,
    zoomIn: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /><line x1="11" y1="8" x2="11" y2="14" /><line x1="8" y1="11" x2="14" y2="11" /></svg>,
    zoomOut: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /><line x1="8" y1="11" x2="14" y2="11" /></svg>,
  };

  return createPortal(
    <div style={S.overlay} onClick={onClose}>
      <style>{`.swal2-container{z-index:10000!important}`}</style>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{ ...S.header, flexWrap: "wrap", gap: isMobile ? "6px" : "12px", padding: isMobile ? "8px 12px" : "12px 24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: isMobile ? "6px" : "16px", flexWrap: "wrap" }}>
            <h3 style={{ margin: 0, fontSize: isMobile ? "13px" : "15px", fontWeight: 700, color: "#1e293b" }}>{title || "Document Editor"}</h3>
            {hasFiles && (
              <div style={{ display: "flex", gap: "3px", alignItems: "center" }}>
                <button onClick={handleUndo} disabled={undoStack.length === 0} title="Undo" style={{ ...S.btn(false), padding: "5px 6px", opacity: undoStack.length === 0 ? 0.4 : 1, cursor: undoStack.length === 0 ? "not-allowed" : "pointer" }}>{SVG.undo}</button>
                <button onClick={handleRedo} disabled={redoStack.length === 0} title="Redo" style={{ ...S.btn(false), padding: "5px 6px", opacity: redoStack.length === 0 ? 0.4 : 1, cursor: redoStack.length === 0 ? "not-allowed" : "pointer" }}>{SVG.redo}</button>
                <button onClick={handleReset} style={{ ...S.btn(false), padding: "5px 8px", fontSize: isMobile ? "10px" : "11px" }}>{isMobile ? "Reset" : "Reset All"}</button>
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
            {hasFiles && !showReview && (
              <button onClick={() => setShowReview(true)} style={{ ...S.btn(false, true), padding: isMobile ? "6px 10px" : "8px 18px", fontSize: isMobile ? "11px" : "12px" }}>
                {isMobile ? "Generate PDF" : "Save & Generate PDF"}
              </button>
            )}
            {hasFiles && showReview && (
              <button onClick={handleSaveAsPdf} disabled={isProcessing} style={{ ...S.btn(false, true), padding: isMobile ? "6px 10px" : "8px 18px", fontSize: isMobile ? "11px" : "12px", opacity: isProcessing ? 0.6 : 1, cursor: isProcessing ? "not-allowed" : "pointer" }}>
                {isProcessing ? "Processing..." : isMobile ? "Export PDF" : "Confirm & Export PDF"}
              </button>
            )}
            <button onClick={onClose} style={{ ...S.btn(false), padding: isMobile ? "6px 10px" : "8px 18px", fontSize: isMobile ? "11px" : "12px" }}>Close</button>
          </div>
        </div>

        {/* PDF Prompt */}
        {pendingFiles.length > 0 && !showReorder && (
          <div style={{ position: "absolute", inset: 0, zIndex: 100, backgroundColor: "rgba(15,23,42,0.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ backgroundColor: "#fff", borderRadius: isMobile ? "12px" : "16px", padding: isMobile ? "24px" : "40px", maxWidth: "440px", width: "90%", textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
              {pendingFiles.some((f) => isDocFile(f)) ? (
                <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.5" style={{ margin: "0 auto 16px" }}>
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="12" y1="12" x2="8" y2="12" /><line x1="16" y1="15" x2="8" y2="15" /><line x1="14" y1="18" x2="8" y2="18" />
                </svg>
              ) : (
                <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.5" style={{ margin: "0 auto 16px" }}>
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
              )}
              <h3 style={{ margin: "0 0 6px", fontSize: isMobile ? "16px" : "17px", fontWeight: 700, color: "#1e293b" }}>{pendingFiles.some((f) => isDocFile(f)) ? "Word File Uploaded" : pendingFiles.some((f) => isXlsxFile(f)) ? "Excel File Uploaded" : pendingFiles.some((f) => isCsvFile(f)) ? "CSV File Uploaded" : pendingFiles.some((f) => isTxtFile(f)) ? "Text File Uploaded" : "File(s) Uploaded"}</h3>
              <p style={{ margin: "0 0 4px", fontSize: isMobile ? "12px" : "13px", color: "#64748b", wordBreak: "break-all" }}>"{pendingFiles.map((f) => f.name).join('", "')}"</p>
              <p style={{ margin: "0 0 24px", fontSize: isMobile ? "12px" : "13px", color: "#94a3b8" }}>{(pendingFiles.some((f) => isDocFile(f)) || pendingFiles.some((f) => isDirectConvertFile(f))) ? "File will be saved in its original format" : pendingFiles.length + " file(s) — how would you like to proceed?"}</p>
              <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: "10px", justifyContent: "center" }}>
                {(() => {
                  const hasNonImage = pendingFiles.some((f) => isDocFile(f) || isDirectConvertFile(f) || f.type === "application/pdf");
                  const hasPassThrough = pendingFiles.some((f) => isDocFile(f) || isXlsxFile(f) || isCsvFile(f) || isTxtFile(f));
                  const passThroughFile = pendingFiles.find((f) => isDocFile(f) || isXlsxFile(f) || isCsvFile(f) || isTxtFile(f));
                  const passThroughLabel = passThroughFile ? (isDocFile(passThroughFile) ? "Upload Word File" : isXlsxFile(passThroughFile) ? "Upload Excel File" : isCsvFile(passThroughFile) ? "Upload CSV File" : "Upload Text File") : "Direct Upload";
                  return (
                    <>
                      <button onClick={handleFilesEdit} disabled={isProcessing || hasNonImage} style={{ ...S.btn(false), padding: isMobile ? "12px 20px" : "12px 24px", fontSize: isMobile ? "12px" : "13px", fontWeight: 600, backgroundColor: "#fff", border: "2px solid #2563eb", color: "#2563eb", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", opacity: isProcessing || hasNonImage ? 0.4 : 1, cursor: isProcessing || hasNonImage ? "not-allowed" : "pointer" }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                        {isProcessing ? "Processing..." : "Edit in Editor"}
                      </button>
                      <button onClick={handleFilesDirect} disabled={isProcessing} style={{ ...S.btn(false, true), padding: isMobile ? "12px 20px" : "12px 24px", fontSize: isMobile ? "12px" : "13px", fontWeight: 600, borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", opacity: isProcessing ? 0.6 : 1, cursor: isProcessing ? "not-allowed" : "pointer" }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>
                        {isProcessing ? "Processing..." : hasPassThrough ? passThroughLabel : "Direct Upload"}
                      </button>
                    </>
                  );
                })()}
              </div>
              <button onClick={() => setPendingFiles([])} disabled={isProcessing} style={{ background: "none", border: "none", color: "#94a3b8", fontSize: isMobile ? "11px" : "12px", cursor: isProcessing ? "not-allowed" : "pointer", marginTop: "16px", textDecoration: "underline" }}>Cancel</button>
            </div>
          </div>
        )}

        {showReorder && (
          <div style={{ position: "absolute", inset: 0, zIndex: 100, backgroundColor: "rgba(15,23,42,0.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ backgroundColor: "#fff", borderRadius: isMobile ? "12px" : "16px", padding: isMobile ? "20px" : "28px", maxWidth: "520px", width: "90%", maxHeight: isMobile ? "85vh" : "none", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
              <h3 style={{ margin: "0 0 4px", fontSize: isMobile ? "15px" : "16px", fontWeight: 700, color: "#1e293b", textAlign: "center" }}>Arrange Pages</h3>
              <p style={{ margin: "0 0 18px", fontSize: isMobile ? "12px" : "13px", color: "#94a3b8", textAlign: "center" }}>{isMobile ? "Tap and hold to reorder" : "Drag to reorder"} — this order will be used in the final PDF</p>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px", maxHeight: isMobile ? "50vh" : "320px", overflowY: "auto", marginBottom: "20px", touchAction: dragIndex !== null ? "none" : "auto" }}
                onTouchMove={handleReorderTouchMove}
                onTouchEnd={handleReorderTouchEnd}>
                {reorderedFiles.map((file, idx) => (
                  <div key={`${file.name}_${idx}`}
                    data-reorder-idx={idx}
                    draggable
                    onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; setDragIndex(idx); setDragOverIndex(null); }}
                    onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (idx !== dragIndex) setDragOverIndex(idx); }}
                    onDragLeave={() => setDragOverIndex(null)}
                    onDrop={(e) => { e.preventDefault(); if (dragIndex !== null && dragIndex !== idx) { reorderPending(dragIndex, idx); } setDragIndex(null); setDragOverIndex(null); }}
                    onDragEnd={() => { setDragIndex(null); setDragOverIndex(null); }}
                    onTouchStart={() => handleReorderTouchStart(idx)}
                    style={{ display: "flex", alignItems: "center", gap: "12px", padding: isMobile ? "10px 10px" : "8px 12px", borderRadius: "10px", border: "1px solid #e2e8f0", backgroundColor: dragIndex === idx ? "#f1f5f9" : "#fff", cursor: "grab", opacity: dragIndex === idx ? 0.5 : 1, outline: dragOverIndex === idx ? "2px dashed #2563eb" : "none", outlineOffset: "2px", transition: "all 0.15s" }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" style={{ flexShrink: 0, touchAction: "none" }}><line x1="8" y1="6" x2="16" y2="6" /><line x1="8" y1="12" x2="16" y2="12" /><line x1="8" y1="18" x2="16" y2="18" /></svg>
                    {file.type === "application/pdf" ? (
                      <svg width={isMobile ? "24" : "28"} height={isMobile ? "28" : "32"} viewBox="0 0 24 28" fill="none" stroke="#2563eb" strokeWidth="1.5" style={{ flexShrink: 0 }}>
                        <path d="M14 2H6a2 2 0 0 0-2 2v20a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="16" y1="14" x2="8" y2="14" />
                        <line x1="16" y1="18" x2="8" y2="18" />
                      </svg>
                    ) : isDocFile(file) ? (
                      <svg width={isMobile ? "24" : "28"} height={isMobile ? "28" : "32"} viewBox="0 0 24 28" fill="none" stroke="#2563eb" strokeWidth="1.5" style={{ flexShrink: 0 }}>
                        <path d="M14 2H6a2 2 0 0 0-2 2v20a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="12" y1="12" x2="8" y2="12" /><line x1="16" y1="15" x2="8" y2="15" /><line x1="14" y1="18" x2="8" y2="18" />
                      </svg>
                    ) : (
                      <div style={{ width: isMobile ? "24px" : "28px", height: isMobile ? "28px" : "32px", borderRadius: "4px", overflow: "hidden", flexShrink: 0, backgroundColor: "#f1f5f9" }}>
                        <img src={URL.createObjectURL(file)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: isMobile ? "11px" : "12px", fontWeight: 600, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</div>
                      <div style={{ fontSize: isMobile ? "10px" : "11px", color: "#94a3b8" }}>{isDocFile(file) ? "Word file" : isXlsxFile(file) ? "Excel file" : isCsvFile(file) ? "CSV file" : isTxtFile(file) ? "Text file" : file.type === "application/pdf" ? "PDF file" : "Image"}</div>
                    </div>
                    <span style={{ fontSize: isMobile ? "10px" : "11px", fontWeight: 700, color: "#94a3b8", background: "#f1f5f9", padding: "2px 8px", borderRadius: "6px", flexShrink: 0 }}>{idx + 1}</span>
                    <button onClick={(e) => { e.stopPropagation(); const copy = [...reorderedFiles]; copy.splice(idx, 1); if (copy.length === 0) { setShowReorder(false); setPendingFiles([]); onClose(); } else { setReorderedFiles(copy); } }}
                      style={{ width: isMobile ? "24px" : "22px", height: isMobile ? "24px" : "22px", borderRadius: "6px", border: "none", backgroundColor: "transparent", color: "#94a3b8", fontSize: isMobile ? "13px" : "12px", lineHeight: 1, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, padding: 0, fontWeight: 600 }}
                      title="Remove file"
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#fee2e2"}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" /></svg>
                    </button>
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", gap: "10px", justifyContent: "center", flexWrap: "wrap" }}>
                <button onClick={() => setShowReorder(false)} disabled={isProcessing} style={{ ...S.btn(false), padding: isMobile ? "10px 18px" : "10px 20px", fontSize: isMobile ? "12px" : "13px", borderRadius: "10px" }}>Back</button>
                <button onClick={handleConfirmReorder} disabled={isProcessing} style={{ ...S.btn(false, true), padding: isMobile ? "10px 18px" : "10px 20px", fontSize: isMobile ? "12px" : "13px", borderRadius: "10px", opacity: isProcessing ? 0.6 : 1, cursor: isProcessing ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: "6px" }}>
                  {isProcessing ? "Generating..." : "Confirm & Generate PDF"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Body */}
        <div style={{ ...S.body, padding: isMobile ? "12px" : "20px" }}>
          {!hasFiles ? (
            <div onDragOver={(e) => e.preventDefault()} onDrop={handleDrop} onClick={() => fileInputRef.current?.click()}
              style={{ border: "2px dashed #cbd5e1", borderRadius: isMobile ? "12px" : "16px", padding: isMobile ? "24px 16px" : "80px 20px", textAlign: "center", cursor: "pointer", backgroundColor: "#fff", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: isMobile ? "auto" : "100%", minHeight: isMobile ? "300px" : "auto", transition: "border-color 0.2s" }}>
              {isMobile ? React.cloneElement(SVG.upload, { width: 32, height: 32 }) : SVG.upload}
              <p style={{ fontSize: isMobile ? "13px" : "16px", fontWeight: 600, color: "#334155", margin: isMobile ? "8px 0 2px" : "12px 0 4px" }}>Drop images here or click to browse</p>
              <p style={{ fontSize: isMobile ? "12px" : "13px", color: "#94a3b8", margin: 0 }}>{getAcceptedText()}</p>
              <input ref={fileInputRef} type="file" accept={getAcceptAttr()} onChange={handleFileSelect} multiple style={{ display: "none" }} />
            </div>
          ) : showReview ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start", height: isMobile ? "auto" : "100%", minHeight: isMobile ? 0 : "auto", paddingTop: isMobile ? "16px" : "0", paddingBottom: isMobile ? "24px" : "0" }}>
              <p style={{ fontSize: isMobile ? "14px" : "15px", fontWeight: 600, color: "#334155", marginBottom: "16px" }}>Review {files.length} page(s) before generating PDF</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: isMobile ? "10px" : "16px", justifyContent: "center", width: "100%" }}>
                {files.map((file, idx) => (
                  <div key={file.id} style={{ position: "relative", width: isMobile ? "calc(50% - 10px)" : "180px", maxWidth: "180px", border: "1px solid #e2e8f0", borderRadius: "12px", overflow: "hidden", backgroundColor: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
                    <button onClick={() => removeFile(idx)}
                      style={{ position: "absolute", top: "4px", right: "4px", zIndex: 2, width: "22px", height: "22px", borderRadius: "6px", border: "none", backgroundColor: "rgba(239,68,68,0.8)", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, fontSize: "11px", fontWeight: 600, lineHeight: 1 }}
                      title="Remove page">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    </button>
                    <img src={previewUrls[file.id] || file.url} alt={file.name} style={{ width: "100%", height: isMobile ? "100px" : "140px", objectFit: "cover", display: "block" }} />
                    <div style={{ padding: isMobile ? "8px" : "10px 12px", fontSize: isMobile ? "11px" : "12px", color: "#64748b" }}>
                      <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#1e293b" }}>{file.name}</div>
                      <div style={{ color: "#94a3b8", marginTop: "2px" }}>{file.width} x {file.height}px</div>
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={() => setShowReview(false)} style={{ ...S.btn(false), marginTop: "20px", padding: isMobile ? "8px 18px" : "10px 24px", fontSize: isMobile ? "12px" : "13px" }}>Back to Editing</button>
            </div>
          ) : activeMode === "perspective" ? (
            <div style={{ display: "flex", flexDirection: "column", height: isMobile ? "auto" : "100%", gap: isMobile ? "6px" : "12px" }}>
              <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "stretch" : "center", gap: isMobile ? "6px" : "0", justifyContent: "space-between", ...S.card, padding: isMobile ? "8px 10px" : "12px 16px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", overflow: "hidden" }}>
                  {files.length > 1 && (
                    <>
                      <button onClick={() => switchToFile(activeIndex > 0 ? activeIndex - 1 : files.length - 1)} style={{ ...S.btn(false), fontSize: isMobile ? "10px" : "12px", padding: "5px 8px" }}>&#9664;</button>
                      <span style={{ fontSize: "11px", fontWeight: 600, color: "#475569", whiteSpace: "nowrap" }}>{activeIndex + 1}/{files.length}</span>
                      <button onClick={() => switchToFile(activeIndex < files.length - 1 ? activeIndex + 1 : 0)} style={{ ...S.btn(false), fontSize: isMobile ? "10px" : "12px", padding: "5px 8px" }}>&#9654;</button>
                    </>
                  )}
                  <span style={{ fontSize: isMobile ? "10px" : "13px", fontWeight: 600, color: "#334155", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{files.length > 1 ? (activeFile?.name || "") : (isMobile ? "Drag corners" : "Drag corners to align document")}</span>
                </div>
                <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                  <button onClick={handleResetPersp} style={{ ...S.btn(false), fontSize: isMobile ? "10px" : "12px", padding: isMobile ? "5px 8px" : "8px 16px" }}>Reset</button>
                  <button onClick={() => setActiveMode("edit")} style={{ ...S.btn(false), fontSize: isMobile ? "10px" : "12px", padding: isMobile ? "5px 8px" : "8px 16px" }}>Cancel</button>
                  <button onClick={handleApplyWarp} style={{ ...S.btn(false, true), backgroundColor: "#10b981", fontSize: isMobile ? "10px" : "12px", padding: isMobile ? "5px 8px" : "8px 16px" }}>Apply Warp</button>
                </div>
              </div>
              <div style={{ flex: 1, ...S.card, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", position: "relative", minHeight: isMobile ? "200px" : "300px", padding: 0, cursor: zoom > 1 ? (isPanning ? "grabbing" : "grab") : "default", userSelect: zoom > 1 ? "none" : "auto", touchAction: zoom > 1 ? "none" : "auto" }}
                onPointerDown={handlePanStart} onPointerMove={handlePanMove} onPointerUp={handlePanEnd} onPointerLeave={handlePanEnd} onSelectStart={(e) => { if (zoom > 1) e.preventDefault(); }}>
                <div style={{ position: "relative", display: "inline-block", padding: isMobile ? "8px" : "20px", transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: "center center", transition: isPanning ? "none" : "transform 0.15s", willChange: "transform", WebkitTransform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, WebkitTransformOrigin: "center center" }}>
                  <img ref={perspImageRef} src={activeFile.url} alt="Perspective" style={{ display: "block", pointerEvents: "none", maxWidth: isMobile ? "95vw" : "80vw", maxHeight: isMobile ? "55vh" : "70vh", WebkitBackfaceVisibility: "hidden", backfaceVisibility: "hidden", imageRendering: "auto" }} onLoad={() => setTimeout(updateRenderSize, 100)} />
                  {perspectivePoints && perspectivePoints.length >= 4 && imgRenderSize.w > 0 && (
                    <svg style={{ position: "absolute", top: isMobile ? 8 : 20, left: isMobile ? 8 : 20, width: imgRenderSize.w, height: imgRenderSize.h, pointerEvents: "none" }}>
                      <polygon points={[0,1,5,2,3,4].map(i => `${perspectivePoints[i].x * imgRenderSize.w},${perspectivePoints[i].y * imgRenderSize.h}`).join(" ")} fill="rgba(16,185,129,0.08)" stroke="#10b981" strokeWidth="2" strokeDasharray="6 3" />
                      {perspectivePoints.slice(0, 6).map((p, i) => {
                        if (i >= 6) return null;
                        const absX = p.x * imgRenderSize.w;
                        const absY = p.y * imgRenderSize.h;
                        const isMid = i >= 4;
                        return <text key={i} x={absX + (i === 1 || i === 2 ? 12 : -22)} y={absY + (i === 2 || i === 3 ? 16 : -8)} fill={isMid ? "#f59e0b" : "#10b981"} fontSize="11" fontWeight="bold" fontFamily="system-ui">{perspLabels[i]}</text>;
                      })}
                    </svg>
                  )}
                  {perspectivePoints && perspectivePoints.length >= 4 && imgRenderSize.w > 0 && perspectivePoints.slice(0, 6).map((p, idx) => (
                    <div key={idx} onPointerDown={(e) => handlePerspPointerDown(idx, e)}
                      style={{ position: "absolute", left: `${p.x * 100}%`, top: `${p.y * 100}%`, width: isMobile ? "44px" : (idx >= 4 ? "28px" : "32px"), height: isMobile ? "44px" : (idx >= 4 ? "28px" : "32px"), transform: "translate(-50%,-50%)", cursor: "move", zIndex: 10, touchAction: "none", WebkitTapHighlightColor: "transparent" }}>
                      <div style={{ width: isMobile ? "44px" : (idx >= 4 ? "28px" : "32px"), height: isMobile ? "44px" : (idx >= 4 ? "28px" : "32px"), borderRadius: "50%", backgroundColor: idx >= 4 ? "rgba(245,158,11,0.15)" : "rgba(16,185,129,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <div style={{ width: isMobile ? "16px" : (idx >= 4 ? "10px" : "14px"), height: isMobile ? "16px" : (idx >= 4 ? "10px" : "14px"), borderRadius: "50%", backgroundColor: "#fff", border: idx >= 4 ? "3px solid #f59e0b" : "3px solid #10b981", boxShadow: "0 1px 6px rgba(0,0,0,0.2)" }} />
                      </div>
                    </div>
                  ))}
                </div>
                {isMobile && (
                  <div style={{ position: "absolute", bottom: 8, left: "50%", transform: "translateX(-50%)", zIndex: 50, display: "flex", gap: "2px", alignItems: "center", backgroundColor: "rgba(255,255,255,0.95)", borderRadius: "6px", padding: "3px", border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
                    <button onClick={() => setZoom(z => Math.max(0.25, z - 0.25))} style={{ ...S.btn(false), padding: "6px 8px", fontSize: "14px", border: "none", display: "flex", alignItems: "center", justifyContent: "center", minWidth: "32px" }}>{SVG.zoomOut}</button>
                    <span style={{ fontSize: "11px", fontWeight: 600, color: "#475569", minWidth: "32px", textAlign: "center" }}>{Math.round(zoom * 100)}%</span>
                    <button onClick={() => setZoom(z => Math.min(4, z + 0.25))} style={{ ...S.btn(false), padding: "6px 8px", fontSize: "14px", border: "none", display: "flex", alignItems: "center", justifyContent: "center", minWidth: "32px" }}>{SVG.zoomIn}</button>
                    <button onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} style={{ ...S.btn(false), padding: "6px 8px", fontSize: "11px", border: "none", minWidth: "32px" }}>Fit</button>
                  </div>
                )}
                {!isMobile && (
                  <div style={{ position: "absolute", top: 8, right: 8, zIndex: 50, display: "flex", gap: "2px", alignItems: "center", backgroundColor: "rgba(255,255,255,0.95)", borderRadius: "6px", padding: "3px", border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
                    <button onClick={() => setZoom(z => Math.max(0.25, z - 0.25))} style={{ ...S.btn(false), padding: "4px 6px", fontSize: "10px", border: "none", display: "flex", alignItems: "center", justifyContent: "center" }}>{SVG.zoomOut}</button>
                    <span style={{ fontSize: "10px", fontWeight: 600, color: "#475569", minWidth: "32px", textAlign: "center" }}>{Math.round(zoom * 100)}%</span>
                    <button onClick={() => setZoom(z => Math.min(4, z + 0.25))} style={{ ...S.btn(false), padding: "4px 6px", fontSize: "10px", border: "none", display: "flex", alignItems: "center", justifyContent: "center" }}>{SVG.zoomIn}</button>
                    <button onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} style={{ ...S.btn(false), padding: "4px 6px", fontSize: "10px", border: "none" }}>Fit</button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? "8px" : "20px", height: isMobile ? "auto" : "100%", overflow: isMobile ? "visible" : "hidden" }}>
              {/* LEFT: Controls */}
              <div style={{ width: isMobile ? "100%" : "300px", flexShrink: 0, display: "flex", flexDirection: "column", gap: isMobile ? "6px" : "16px", order: isMobile ? 1 : 0 }}>
                {/* Edit Desk Card */}
                <div style={{ ...S.card, padding: isMobile ? "10px" : "16px" }}>
                  <div style={S.label}>Editing Desk</div>
                  <div style={{ display: "flex", borderRadius: "8px", overflow: "hidden", border: "1px solid #e2e8f0", marginBottom: "16px", WebkitTapHighlightColor: "transparent" }}>
                    {[{ key: "scanner", icon: SVG.scanner, label: "Scanner" }, { key: "presets", icon: SVG.filters, label: "Filters" }, { key: "adjust", icon: SVG.sliders, label: "Sliders" }, { key: "layout", icon: SVG.layout, label: "Layout" }].map((tab) => (
                      <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                        style={{ flex: 1, padding: isMobile ? "6px 2px" : "10px 6px", border: "none", cursor: "pointer", fontSize: isMobile ? "10px" : "12px", fontWeight: 600, backgroundColor: activeTab === tab.key ? "#2563eb" : "transparent", color: activeTab === tab.key ? "#fff" : "#64748b", transition: "all 0.15s", display: "flex", alignItems: "center", justifyContent: "center", gap: "4px", WebkitTapHighlightColor: "transparent" }}>
                        <span style={{ fontSize: isMobile ? "10px" : "11px" }}>{tab.icon}</span> {tab.label}
                      </button>
                    ))}
                  </div>

                  {activeTab === "scanner" && (
                    <div>
                      <div style={{ ...S.label, marginBottom: "8px" }}>Scanner Mode</div>
                      <p style={{ fontSize: "11px", color: "#64748b", margin: "0 0 12px", lineHeight: "1.4" }}>One-tap scanner-quality enhancement. Auto-whitens background, sharpens text, flattens perspective.</p>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                        {[
                          { type: "scanner-auto", label: "Auto Scan", desc: "Full scanner pipeline" },
                          { type: "scanner-bw", label: "B&W Scan", desc: "Clean monochrome scan" },
                          { type: "scanner-color", label: "Color Scan", desc: "Enhanced color document" },
                          { type: "original", label: "Original", desc: "No processing" },
                        ].map((preset) => (
                          <button key={preset.type} onClick={() => handleFilterTypeSelect(preset.type)}
                            style={{ padding: isMobile ? "6px 8px" : "10px 12px", borderRadius: "10px", border: filterConfig.filterType === preset.type ? "2px solid #2563eb" : "1px solid #e2e8f0", cursor: "pointer", textAlign: "left", backgroundColor: filterConfig.filterType === preset.type ? "#eff6ff" : "#fff", transition: "all 0.15s" }}>
                            <div style={{ fontWeight: 700, fontSize: isMobile ? "10px" : "12px", color: filterConfig.filterType === preset.type ? "#2563eb" : "#334155", marginBottom: "2px" }}>{preset.label}</div>
                            <div style={{ fontSize: isMobile ? "9px" : "10px", color: "#94a3b8", lineHeight: "1.3" }}>{preset.desc}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {activeTab === "presets" && (
                    <div>
                      <div style={{ ...S.label, marginBottom: isMobile ? "6px" : "8px" }}>Optimization Presets</div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: isMobile ? "4px" : "8px" }}>
                        {filterPresets.map((preset) => (
                          <button key={preset.type} onClick={() => handleFilterTypeSelect(preset.type)}
                            style={{ padding: isMobile ? "6px 8px" : "10px 12px", borderRadius: "10px", border: filterConfig.filterType === preset.type ? "2px solid #2563eb" : "1px solid #e2e8f0", cursor: "pointer", textAlign: "left", backgroundColor: filterConfig.filterType === preset.type ? "#eff6ff" : "#fff", transition: "all 0.15s" }}>
                            <div style={{ fontWeight: 700, fontSize: isMobile ? "10px" : "12px", color: filterConfig.filterType === preset.type ? "#2563eb" : "#334155", marginBottom: "2px" }}>{preset.label}</div>
                            <div style={{ fontSize: isMobile ? "9px" : "10px", color: "#94a3b8", lineHeight: "1.3" }}>{preset.desc}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {activeTab === "adjust" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: isMobile ? "6px" : "14px" }}>
                      {[{ key: "brightness", label: "Brightness", min: -100, max: 100 }, { key: "contrast", label: "Contrast", min: -100, max: 100 }, { key: "saturation", label: "Saturation", min: -100, max: 100 }, { key: "sharpness", label: "Sharpness", min: 0, max: 100 }].map((s) => (
                        <div key={s.key}>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: isMobile ? "10px" : "12px", fontWeight: 500, color: "#475569", marginBottom: "2px" }}>
                            <span>{s.label}</span>
                            <span style={{ fontFamily: "monospace", color: "#94a3b8" }}>{filterConfig[s.key]}%</span>
                          </div>
                          <input type="range" min={s.min} max={s.max} value={filterConfig[s.key]}
                            onChange={(e) => handleSliderChange(s.key, parseInt(e.target.value))}
                            style={{ width: "100%", accentColor: "#2563eb", cursor: "pointer", height: isMobile ? "6px" : "4px" }} />
                        </div>
                      ))}
                    </div>
                  )}

                  {activeTab === "layout" && (
                    <div>
                      <div style={{ ...S.label, marginBottom: isMobile ? "6px" : "10px" }}>Quick Transforms</div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: isMobile ? "4px" : "8px", marginBottom: isMobile ? "12px" : "20px" }}>
                        {[
                          { fn: handleRotate, icon: SVG.rotate, label: "Rotate 90" },
                          { fn: handleFlipH, icon: SVG.flipH, label: "Flip H" },
                          { fn: handleFlipV, icon: SVG.flipV, label: "Flip V" },
                        ].map((t, i) => (
                          <button key={i} onClick={t.fn} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: isMobile ? "3px" : "6px", padding: isMobile ? "8px 4px" : "12px 6px", borderRadius: "10px", border: "1px solid #e2e8f0", backgroundColor: "#fff", color: "#475569", cursor: "pointer", fontSize: isMobile ? "9px" : "10px", fontWeight: 600, transition: "all 0.15s" }}>
                            {t.icon} {t.label}
                          </button>
                        ))}
                      </div>
                      <div style={{ ...S.label, marginBottom: isMobile ? "6px" : "10px" }}>Crop Presets</div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: isMobile ? "4px" : "8px" }}>
                        {aspectRatios.map((ar) => (
                          <button key={ar.ratio} onClick={() => { pushHistory(); updateActiveState({ cropAspect: ar.ratio }); setCompareMode(false); }}
                            style={{ padding: isMobile ? "6px 6px" : "10px 8px", borderRadius: "10px", border: cropAspect === ar.ratio ? "2px solid #2563eb" : "1px solid #e2e8f0", cursor: "pointer", fontSize: isMobile ? "10px" : "11px", fontWeight: 600, backgroundColor: cropAspect === ar.ratio ? "#eff6ff" : "#fff", color: cropAspect === ar.ratio ? "#2563eb" : "#475569", transition: "all 0.15s" }}>
                            {ar.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* RIGHT: Preview */}
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: isMobile ? "6px" : "12px", minWidth: 0, order: isMobile ? 0 : 0, minHeight: isMobile ? "40vh" : 0 }}>
                {/* Mode bar */}
                <div style={{ ...S.card, padding: isMobile ? "6px 8px" : "10px 16px", display: "flex", flexDirection: isMobile ? "row" : "row", gap: isMobile ? "6px" : "0", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
                  <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                    <button onClick={() => setActiveMode("edit")} style={{ ...S.btn(activeMode === "edit", activeMode === "edit"), fontSize: isMobile ? "10px" : "12px", padding: isMobile ? "5px 8px" : "8px 16px" }}>{isMobile ? "Workbench" : "Visual Workbench"}</button>
                    <button onClick={enterPerspectiveMode} style={{ ...S.btn(activeMode === "perspective", activeMode === "perspective"), fontSize: isMobile ? "10px" : "12px", padding: isMobile ? "5px 8px" : "8px 16px" }}>{isMobile ? "Perspective" : "Perspective Align"}</button>
                  </div>
                  <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                    {previewUrl && (
                      <button onClick={() => setCompareMode((v) => !v)} style={{ ...S.btn(compareMode), fontSize: isMobile ? "10px" : "12px", padding: isMobile ? "5px 8px" : "8px 16px" }}>
                        {SVG.compare} {compareMode ? "Single" : "Compare"}
                      </button>
                    )}
                    <button onClick={() => addMoreRef.current?.click()} style={{ ...S.btn(false), fontSize: isMobile ? "10px" : "12px", padding: isMobile ? "5px 8px" : "8px 16px" }}>
                      {SVG.plus} {isMobile ? "Add" : "Add Pages"}
                    </button>
                    <input ref={addMoreRef} type="file" accept={getAcceptAttr()} onChange={handleFileSelect} multiple style={{ display: "none" }} />
                  </div>
                </div>

                {/* Preview area */}
                <div style={{ ...S.card, flex: 1, padding: 0, overflow: "hidden", display: "flex", flexDirection: "column", alignItems: "stretch", justifyContent: "stretch", minHeight: isMobile ? "200px" : "320px", backgroundColor: "#fafbfc", position: "relative", cursor: (zoom > 1 && !compareMode) ? (isPanning ? "grabbing" : "grab") : "default", userSelect: zoom > 1 ? "none" : "auto", touchAction: zoom > 1 ? "none" : "auto", WebkitOverflowScrolling: "touch" }}
                  onPointerDown={!compareMode ? handlePanStart : undefined} onPointerMove={!compareMode ? handlePanMove : undefined} onPointerUp={handlePanEnd} onPointerLeave={handlePanEnd} onSelectStart={(e) => { if (zoom > 1) e.preventDefault(); }}>
                  {compareMode && previewUrl ? (
                    <div style={{ flex: 1, position: "relative", userSelect: "none", overflow: "hidden" }}>
                      <img src={activeFile.url} alt="Original" style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "contain", WebkitBackfaceVisibility: "hidden", backfaceVisibility: "hidden" }} />
                      <img src={previewUrl} alt="Enhanced" style={{
                        position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "contain",
                        maskImage: `linear-gradient(to right, transparent 0%, transparent ${Math.max(0, sliderPos - 3)}%, white ${sliderPos + 3}%, white 100%)`,
                        WebkitMaskImage: `linear-gradient(to right, transparent 0%, transparent ${Math.max(0, sliderPos - 3)}%, white ${sliderPos + 3}%, white 100%)`,
                      }} />
                      <div style={{ position: "absolute", top: 0, left: `${sliderPos}%`, width: "3px", height: "100%", backgroundColor: "#2563eb", zIndex: 10, pointerEvents: "none", transform: "translateX(-50%)", boxShadow: "0 0 8px rgba(37,99,235,0.3)" }} />
                      <div style={{ position: "absolute", top: "50%", left: `${sliderPos}%`, transform: "translate(-50%,-50%)", zIndex: 11, pointerEvents: "none", width: isMobile ? "30px" : "36px", height: isMobile ? "30px" : "36px", borderRadius: "50%", backgroundColor: "#fff", border: "3px solid #2563eb", boxShadow: "0 2px 12px rgba(0,0,0,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 3 12 9 6" /><polyline points="15 6 21 12 15 18" /></svg>
                      </div>
                      <input type="range" min="0" max="100" value={sliderPos} onChange={(e) => setSliderPos(parseInt(e.target.value))} style={{ position: "absolute", inset: 0, opacity: 0, width: "100%", height: "100%", cursor: "ew-resize", zIndex: 20 }} />
                      <span style={{ position: "absolute", top: isMobile ? "6px" : "12px", right: isMobile ? "6px" : "12px", backgroundColor: "rgba(30,41,59,0.85)", color: "#fff", fontSize: isMobile ? "9px" : "10px", fontWeight: 700, padding: isMobile ? "3px 8px" : "4px 12px", borderRadius: "6px", fontFamily: "system-ui, sans-serif", zIndex: 5, letterSpacing: "0.5px" }}>ORIGINAL</span>
                      <span style={{ position: "absolute", top: isMobile ? "6px" : "12px", left: isMobile ? "6px" : "12px", backgroundColor: "rgba(37,99,235,0.9)", color: "#fff", fontSize: isMobile ? "9px" : "10px", fontWeight: 700, padding: isMobile ? "3px 8px" : "4px 12px", borderRadius: "6px", fontFamily: "system-ui, sans-serif", zIndex: 5, letterSpacing: "0.5px" }}>ENHANCED</span>
                    </div>
                  ) : (
                    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                      <img src={previewUrl || activeFile.url} alt="Preview" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block", pointerEvents: "none", transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: "center center", transition: isPanning ? "none" : "transform 0.15s", WebkitTransform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, WebkitTransformOrigin: "center center", willChange: "transform", WebkitBackfaceVisibility: "hidden", backfaceVisibility: "hidden" }} />
                    </div>
                  )}
                  {!compareMode && !isMobile && (
                    <div style={{ position: "absolute", top: 8, right: 8, zIndex: 50, display: "flex", gap: "2px", alignItems: "center", backgroundColor: "rgba(255,255,255,0.95)", borderRadius: "6px", padding: "3px", border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
                      <button onClick={() => setZoom(z => Math.max(0.25, z - 0.25))} style={{ ...S.btn(false), padding: "4px 6px", fontSize: "10px", border: "none", display: "flex", alignItems: "center", justifyContent: "center" }}>{SVG.zoomOut}</button>
                      <span style={{ fontSize: "10px", fontWeight: 600, color: "#475569", minWidth: "32px", textAlign: "center" }}>{Math.round(zoom * 100)}%</span>
                      <button onClick={() => setZoom(z => Math.min(4, z + 0.25))} style={{ ...S.btn(false), padding: "4px 6px", fontSize: "10px", border: "none", display: "flex", alignItems: "center", justifyContent: "center" }}>{SVG.zoomIn}</button>
                      <button onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} style={{ ...S.btn(false), padding: "4px 6px", fontSize: "10px", border: "none" }}>Fit</button>
                    </div>
                  )}
                  {compareMode && !isMobile && (
                    <div style={{ position: "absolute", top: 8, right: 8, zIndex: 50, display: "flex", gap: "2px", alignItems: "center", backgroundColor: "rgba(255,255,255,0.95)", borderRadius: "6px", padding: "3px", border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
                      <button onClick={() => setZoom(z => Math.max(0.25, z - 0.25))} style={{ ...S.btn(false), padding: "4px 6px", fontSize: "10px", border: "none", display: "flex", alignItems: "center", justifyContent: "center" }}>{SVG.zoomOut}</button>
                      <span style={{ fontSize: "10px", fontWeight: 600, color: "#475569", minWidth: "32px", textAlign: "center" }}>{Math.round(zoom * 100)}%</span>
                      <button onClick={() => setZoom(z => Math.min(4, z + 0.25))} style={{ ...S.btn(false), padding: "4px 6px", fontSize: "10px", border: "none", display: "flex", alignItems: "center", justifyContent: "center" }}>{SVG.zoomIn}</button>
                      <button onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} style={{ ...S.btn(false), padding: "4px 6px", fontSize: "10px", border: "none" }}>Fit</button>
                    </div>
                  )}
                  {!compareMode && isMobile && (
                    <div style={{ position: "absolute", bottom: 8, left: "50%", transform: "translateX(-50%)", zIndex: 50, display: "flex", gap: "2px", alignItems: "center", backgroundColor: "rgba(255,255,255,0.95)", borderRadius: "6px", padding: "3px", border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
                      <button onClick={() => setZoom(z => Math.max(0.25, z - 0.25))} style={{ ...S.btn(false), padding: "6px 8px", fontSize: "14px", border: "none", display: "flex", alignItems: "center", justifyContent: "center", minWidth: "32px" }}>{SVG.zoomOut}</button>
                      <span style={{ fontSize: "11px", fontWeight: 600, color: "#475569", minWidth: "32px", textAlign: "center" }}>{Math.round(zoom * 100)}%</span>
                      <button onClick={() => setZoom(z => Math.min(4, z + 0.25))} style={{ ...S.btn(false), padding: "6px 8px", fontSize: "14px", border: "none", display: "flex", alignItems: "center", justifyContent: "center", minWidth: "32px" }}>{SVG.zoomIn}</button>
                      <button onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} style={{ ...S.btn(false), padding: "6px 8px", fontSize: "11px", border: "none", minWidth: "32px" }}>Fit</button>
                    </div>
                  )}
                  {compareMode && isMobile && (
                    <div style={{ position: "absolute", bottom: 8, left: "50%", transform: "translateX(-50%)", zIndex: 50, display: "flex", gap: "2px", alignItems: "center", backgroundColor: "rgba(255,255,255,0.95)", borderRadius: "6px", padding: "3px", border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
                      <button onClick={() => setZoom(z => Math.max(0.25, z - 0.25))} style={{ ...S.btn(false), padding: "6px 8px", fontSize: "14px", border: "none", display: "flex", alignItems: "center", justifyContent: "center", minWidth: "32px" }}>{SVG.zoomOut}</button>
                      <span style={{ fontSize: "11px", fontWeight: 600, color: "#475569", minWidth: "32px", textAlign: "center" }}>{Math.round(zoom * 100)}%</span>
                      <button onClick={() => setZoom(z => Math.min(4, z + 0.25))} style={{ ...S.btn(false), padding: "6px 8px", fontSize: "14px", border: "none", display: "flex", alignItems: "center", justifyContent: "center", minWidth: "32px" }}>{SVG.zoomIn}</button>
                      <button onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} style={{ ...S.btn(false), padding: "6px 8px", fontSize: "11px", border: "none", minWidth: "32px" }}>Fit</button>
                    </div>
                  )}
                </div>

                {/* File stats */}
                <div style={{ ...S.card, padding: isMobile ? "4px 8px" : "10px 16px", display: "flex", gap: isMobile ? "4px 8px" : "20px", fontSize: isMobile ? "9px" : "12px", flexWrap: "wrap" }}>
                  <span style={{ color: "#64748b" }}>Size: <strong style={{ color: "#1e293b" }}>{formatBytes(activeFile.file?.size)}</strong></span>
                  {!isMobile && <span style={{ color: "#e2e8f0" }}>|</span>}
                  <span style={{ color: "#64748b" }}>Dims: <strong style={{ color: "#1e293b" }}>{activeFile.width}x{activeFile.height}</strong></span>
                  {!isMobile && <span style={{ color: "#e2e8f0" }}>|</span>}
                  <span style={{ color: "#64748b" }}>Pages: <strong style={{ color: "#1e293b" }}>{files.length}</strong></span>
                </div>

                {/* Thumbnails */}
                <div style={{ display: "flex", gap: isMobile ? "4px" : "8px", alignItems: "center", overflowX: isMobile ? "auto" : "visible", flexWrap: isMobile ? "nowrap" : "wrap", paddingBottom: isMobile ? "2px" : "0", touchAction: dragIndex !== null ? "none" : "auto", WebkitOverflowScrolling: "touch" }}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}>
                  {files.length > 1 && (
                    <span style={{ fontSize: "10px", color: "#94a3b8", marginRight: "4px", userSelect: "none", flexShrink: 0 }}>{isMobile ? "Reorder" : "Drag to reorder"}</span>
                  )}
                  {files.map((file, idx) => (
                    <div key={file.id}
                      data-thumb-idx={idx}
                      draggable
                      onClick={() => switchToFile(idx)}
                      onDragStart={(e) => handleDragStart(idx, e)}
                      onDragOver={(e) => handleDragOver(idx, e)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleThumbDrop(idx, e)}
                      onDragEnd={handleDragEnd}
                      onTouchStart={(e) => handleTouchStart(idx, e)}
                      title={`${file.name} — drag to reorder`}
                      style={{ minWidth: isMobile ? "50px" : "60px", height: isMobile ? "50px" : "60px", borderRadius: "10px", overflow: "hidden", border: idx === activeIndex ? "2px solid #2563eb" : "1px solid #e2e8f0", cursor: "grab", position: "relative", flexShrink: 0, boxShadow: idx === activeIndex ? "0 0 0 3px rgba(37,99,235,0.12)" : "none", transition: "all 0.15s", opacity: dragIndex === idx ? 0.4 : 1, outline: dragOverIndex === idx ? "2px dashed #2563eb" : "none", outlineOffset: "2px" }}>
                      <img src={file.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", pointerEvents: "none" }} />
                      <button onClick={(e) => { e.stopPropagation(); removeFile(idx); }} style={{ position: "absolute", top: 0, right: 0, width: isMobile ? "16px" : "18px", height: isMobile ? "16px" : "18px", borderRadius: "0 0 0 8px", border: "none", backgroundColor: "rgba(239,68,68,0.85)", color: "#fff", fontSize: isMobile ? "9px" : "10px", lineHeight: isMobile ? "16px" : "18px", textAlign: "center", cursor: "pointer", padding: 0, fontWeight: 600 }}>x</button>
                      {files.length > 1 && (
                        <span style={{ position: "absolute", bottom: 0, left: 0, backgroundColor: "rgba(30,41,59,0.7)", color: "#fff", fontSize: "9px", fontWeight: 700, lineHeight: "14px", padding: "0 5px", borderRadius: "0 4px 0 0" }}>{idx + 1}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
